/**
 * MUTAGIM.SHOP — Admin Fraud Dashboard (Phase 10)
 * Gestion des commandes suspectes et frauduleuses
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, AlertTriangle, CheckCircle, XCircle, Eye, Clock,
  Loader2, RefreshCw, Filter
, ArrowLeft } from 'lucide-react';
import { AdminPageWrapper } from '@/components/admin/AdminPageWrapper';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminFraudPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState(null);
  const [reviewModal, setReviewModal] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchFraudOrders();
  }, [filter]);

  const fetchFraudOrders = async () => {
    setLoading(true);
    try {
      const url = new URL(`${API_URL}/api/admin/security/fraud`);
      if (filter) url.searchParams.set('status', filter);

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
        setStats(data.stats || {});
      }
    } catch (error) {
      console.error('Error fetching fraud orders:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (action) => {
    if (!reviewModal) return;

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/security/fraud/${reviewModal.id}/review`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action,
          notes: reviewNotes,
        })
      });

      if (response.ok) {
        toast.success(`Commande ${action === 'approve' ? 'approuvée' : 'rejetée'}`);
        setReviewModal(null);
        setReviewNotes('');
        fetchFraudOrders();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erreur');
      }
    } catch (error) {
      console.error('Error reviewing order:', error);
      toast.error('Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const getRiskColor = (score) => {
    if (score >= 75) return 'text-red-500';
    if (score >= 50) return 'text-orange-500';
    if (score >= 25) return 'text-yellow-500';
    return 'text-green-500';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'blocked':
        return <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded">Bloquée</span>;
      case 'pending_review':
        return <span className="px-2 py-1 bg-orange-500/20 text-orange-400 text-xs rounded">En review</span>;
      case 'approved':
        return <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded">Approuvée</span>;
      case 'rejected':
        return <span className="px-2 py-1 bg-gray-500/20 text-gray-200 text-xs rounded">Rejetée</span>;
      default:
        return <span className="px-2 py-1 bg-gray-500/20 text-gray-200 text-xs rounded">{status}</span>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <AdminPageWrapper title="Anti-Fraude" subtitle="Gestion des commandes suspectes">
      <div className="space-y-6" data-testid="admin-fraud-page">
        {/* Actions */}
        <div className="flex gap-2 justify-end">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/security/blacklist')}
          >
            Blacklist
          </Button>
          <Button
            variant="outline"
            onClick={fetchFraudOrders}
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { key: 'pending_review', label: 'En review', value: stats.pending_review || 0, color: 'text-orange-400', icon: Clock },
            { key: 'blocked', label: 'Bloquées', value: stats.blocked || 0, color: 'text-red-400', icon: XCircle },
            { key: 'approved', label: 'Approuvées', value: stats.approved || 0, color: 'text-green-400', icon: CheckCircle },
            { key: 'rejected', label: 'Rejetées', value: stats.rejected || 0, color: 'text-gray-200', icon: AlertTriangle },
          ].map(stat => (
            <button
              key={stat.key}
              onClick={() => setFilter(filter === stat.key ? null : stat.key)}
              className={cn(
                "p-4 rounded-lg border transition-colors text-left",
                filter === stat.key 
                  ? "bg-gray-800 border-mutagim-red" 
                  : "bg-gray-900 border-gray-800 hover:border-gray-700"
              )}
            >
              <div className="flex items-center gap-2 mb-2">
                <stat.icon className={cn("w-5 h-5", stat.color)} />
                <span className="text-gray-200 text-sm">{stat.label}</span>
              </div>
              <p className={cn("text-2xl font-bold", stat.color)}>{stat.value}</p>
            </button>
          ))}
        </div>

        {/* Stripe Radar Info */}
        <div className="mb-6 p-4 bg-blue-900/30 border border-blue-500/30 rounded-lg">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-blue-400" />
            <div>
              <p className="text-white font-medium">Stripe Radar Activé</p>
              <p className="text-sm text-gray-200">
                Score de risque &gt; 75 → Blocage automatique | Score 50-75 → Review manuelle
              </p>
            </div>
          </div>
        </div>

        {/* Liste des commandes */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-mutagim-red" />
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16">
            <Shield className="w-16 h-16 mx-auto mb-4 text-green-500" />
            <p className="text-gray-200">Aucune commande suspecte</p>
          </div>
        ) : (
          <div className="bg-gray-900 rounded-lg border border-gray-800 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-200">Commande</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-200">Client</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-200">Montant</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-200">Score</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-200">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-200">Date</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-200">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {orders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-800/50">
                    <td className="px-4 py-3">
                      <span className="text-white font-mono text-sm">{order.order_id}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm text-white">{order.customer_email}</p>
                        {order.ip_address && (
                          <p className="text-xs text-gray-500">IP: {order.ip_address}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-white">{order.total?.toFixed(2)} {order.currency}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={cn(
                        "text-lg font-bold",
                        getRiskColor(order.risk_score || 0)
                      )}>
                        {order.risk_score ?? '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {getStatusBadge(order.fraud_status)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-200">{formatDate(order.created_at)}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {(order.fraud_status === 'pending_review' || order.fraud_status === 'blocked') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setReviewModal(order); setReviewNotes(''); }}
                          className="border-gray-700 text-white"
                        >
                          <Eye className="w-4 h-4 mr-1" /> Review
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal Review */}
        {reviewModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 rounded-lg w-full max-w-lg border border-gray-800">
              <div className="p-6 border-b border-gray-800">
                <h3 className="text-lg font-semibold text-white">
                  Review: {reviewModal.order_id}
                </h3>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-200 text-sm">Email</p>
                    <p className="text-white">{reviewModal.customer_email}</p>
                  </div>
                  <div>
                    <p className="text-gray-200 text-sm">Montant</p>
                    <p className="text-white">{reviewModal.total?.toFixed(2)} {reviewModal.currency}</p>
                  </div>
                  <div>
                    <p className="text-gray-200 text-sm">Score de risque</p>
                    <p className={cn("text-2xl font-bold", getRiskColor(reviewModal.risk_score || 0))}>
                      {reviewModal.risk_score ?? 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-200 text-sm">IP</p>
                    <p className="text-white font-mono text-sm">{reviewModal.ip_address || 'N/A'}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-200 mb-2">Notes (optionnel)</label>
                  <Textarea
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    placeholder="Raison de la décision..."
                    className="bg-gray-800 border-gray-700 text-white"
                    rows={3}
                  />
                </div>
              </div>

              <div className="p-6 border-t border-gray-800 flex justify-end gap-3">
                <Button
                  variant="ghost"
                  onClick={() => setReviewModal(null)}
                  className="text-gray-200"
                >
                  Annuler
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleReview('reject')}
                  disabled={submitting}
                  className="border-red-500 text-red-400 hover:bg-red-500/10"
                >
                  <XCircle className="w-4 h-4 mr-2" /> Rejeter
                </Button>
                <Button
                  onClick={() => handleReview('approve')}
                  disabled={submitting}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" /> Approuver
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminPageWrapper>
  );
}

export default AdminFraudPage;
